import { useQuery } from '@tanstack/react-query';
import {
  sellableCatalogService,
  type SellableCatalog,
  type SellableItem,
} from '@app/shared/services/sellable-catalog.service';

export const sellableCatalogKeys = {
  all: ['sellable-catalog'] as const,
};

const EMPTY_CORE: SellableItem = {
  code: '',
  kind: 'core',
  category: '',
  monthlyPriceUsd: 0,
  currency: 'DOP',
  unlocks: [],
};

export const useSellableCatalog = () =>
  useQuery<SellableCatalog>({
    queryKey: sellableCatalogKeys.all,
    queryFn: () => sellableCatalogService.getCatalog(),
    // 60s stale — a SADMIN price change should reflect on /account
    // within a tab refocus, not after a 5-minute window. The
    // public catalog endpoint is cheap to re-fetch.
    staleTime: 60 * 1000,
    // Normalize the shape so consumers can call catalog.items.find /
    // catalog.quantityScales.map without a null-guard. An unexpected
    // response (error object, empty body) otherwise leaves `items`
    // undefined and crashes with "Cannot read properties of undefined
    // (reading 'find')" inside useModulePaywall → PlanGuard.
    select: (data): SellableCatalog => ({
      core: data?.core ?? EMPTY_CORE,
      items: Array.isArray(data?.items) ? data.items : [],
      quantityScales: Array.isArray(data?.quantityScales)
        ? data.quantityScales
        : [],
      // Preserve the module → capabilities map. /account's
      // available-addons filter unions this with owned codes to
      // suppress redundant addons (e.g. hide `addon.kds` when
      // `module.kds` is already owned via a bundled plan).
      moduleUnlocks:
        data?.moduleUnlocks && typeof data.moduleUnlocks === 'object'
          ? data.moduleUnlocks
          : {},
    }),
  });
